"use client";

import React from "react";

import ScienceSectionIntro from "@/components/common/ScienceSectionIntro/ScienceSectionIntro";
import { ButtonNova } from "@/components/ui-kit/ButtonNova/ButtonNova";
import Picture from "@/components/ui-kit/Picture/Picture";
import { TextElementNova } from "@/components/ui-kit/TextElementNova/TextElementNova";
import { useNavigation } from "@/hooks/useNavigation";
import { cn } from "@/utils/general";
import { segmentEvent } from "@/utils/segmentfunctions";

export type ScienceStandardsProps = {
  id?: string;
  intro?: {
    tag?: string;
    headline?: string;
    text?: string;
  };
  image: {
    mobile: string;
    tablet: string;
    desktop: string;
    alt: string;
  };
  faqs: { title: string }[];
  learnMore?: {
    label: string;
    linkTo: string;
  };
};

export default function ScienceStandards({
  intro,
  id,
  image,
  faqs,
  learnMore,
}: ScienceStandardsProps) {
  const navigate = useNavigation();

  const handleCtaClick = () => {
    if (!learnMore) return;
    segmentEvent("Marketing CTA Clicked", {
      destination: learnMore.linkTo,
      module_location: "mid_fold",
      text: learnMore.label,
      type: "button",
    });
    navigate(learnMore.linkTo);
  };

  return (
    <section
      data-section
      id={id}
      className="relative z-[1] py-8 md:py-12 xl:py-20 bg-nova-base-solid-10"
    >
      <div className="w-full max-w-[1440px] mx-auto md:px-8">
        {intro && <ScienceSectionIntro {...intro} headingWidth={0.55} />}

        <div className="flex flex-wrap gap-10 md:gap-6 xl:gap-20">
          <div className="w-full md:w-[calc(50%-12px)] xl:w-[540px]">
            <Picture
              src={image.desktop}
              sources={[
                { media: "(max-width: 767px)", srcSet: image.mobile },
                {
                  media: "(min-width: 768px) and (max-width: 1023px)",
                  srcSet: image.tablet,
                },
              ]}
              alt={image.alt || ""}
              role="presentation"
            />
          </div>
          <div className="w-full md:w-[calc(50%-12px)] xl:w-[calc(100%-564px)] 3xl:w-[calc(100%-620px)] flex flex-col xl:px-10">
            <ul
              className={cn(
                "relative flex flex-col md:gap-y-2 mx-6 md:mx-0",
                "before:content-[''] before:absolute before:top-0 before:-left-px before:w-full before:h-2",
                "before:border-t before:border-x before:border-nova-transparency-black-20 before:rounded-t",
                "after:content-[''] after:absolute after:bottom-0 after:-left-px after:w-full after:h-2",
                "after:border-b after:border-x after:border-nova-transparency-black-20 after:rounded-b"
              )}
            >
              {faqs.map((item, index) => (
                <li
                  key={index}
                  className="border-t border-nova-transparency-black-20 first:border-t-0"
                >
                  <div className="flex p-4 gap-4 md:pb-[13px] min-[840px]:py-[min(2.6vw,36px)] min-[840px]:px-4">
                    <span className="w-6 h-6 bg-nova-brand-accent-80 rounded-sm flex justify-center items-center shrink-0">
                      <TextElementNova
                        element="span"
                        className="CTA-10-Dia-Normal"
                        text={String(index + 1)}
                      />
                    </span>
                    <TextElementNova className="Body-20-Dia-Normal" text={item.title} />
                  </div>
                </li>
              ))}
            </ul>
            {learnMore && (
              <div className="pt-8 pb-2 md:pb-0 mt-auto">
                <ButtonNova
                  icon="Right"
                  className="mx-auto"
                  label={learnMore.label}
                  onClick={handleCtaClick}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
